import { forwardRef, type HTMLAttributes, type KeyboardEvent } from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { useThemeMode } from "@/lib/theme";
import { cn } from "@/lib/cn";
import { Tooltip } from "./Tooltip";

// Stage 6: local replacement for @datasirpi/ui's <ThemeSwitcher>. Reads
// from and writes through this app's own `useThemeMode` (@/lib/theme) —
// not @datasirpi/theme's — so it no longer requires a vendor
// `<ThemeProvider>` ancestor. Layout/CSS ported letter-for-letter from
// the vendor's compiled bundle and its ThemeSwitcher CSS. Icons switched
// to lucide-react, same reasoning as Checkbox/Stepper/Banner.
//
// Phase 5.1 / decision D3: "system" is now a real, listening mode and the
// default — see src/lib/theme.tsx. Stage 6 deliberately left it out because
// it "would need real matchMedia listening"; that listening now exists, so
// the third segment is no longer a label over nothing.
//
// It sits FIRST because it is the default: the control then reads as "the
// automatic one, or override it either way", which is the order Fibery's
// and Grok's appearance settings both use (Refero research). Three
// segments rather than a dropdown for the same reason those do — the
// choice and the current state stay visible at a glance, and a SOC console
// should never hide which theme it is in.
//
// A real "hc" (high-contrast) mode is still out: it would need its own
// palette, and there is none anywhere in this app's CSS.
export interface ThemeSwitcherProps extends HTMLAttributes<HTMLDivElement> {
  "aria-label"?: string;
}

const OPTIONS = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
] as const;

export const ThemeSwitcher = forwardRef<HTMLDivElement, ThemeSwitcherProps>(function ThemeSwitcher(
  { className, "aria-label": ariaLabel = "Theme", ...rest },
  ref
) {
  const { mode, resolvedMode, setMode } = useThemeMode();

  // The wipe in globals.css reads its centre off these two custom properties,
  // so it can start from the control the person actually pressed instead of
  // from the middle of the screen. Written on <html> just before the mode
  // changes, which is while the View Transition is still capturing the old
  // frame. Any other caller of setMode simply inherits the last value, or the
  // 50%/50% default the keyframes fall back to.
  function setWipeOrigin(el: HTMLElement) {
    const r = el.getBoundingClientRect();
    const root = document.documentElement;
    root.style.setProperty("--theme-wipe-x", `${r.left + r.width / 2}px`);
    root.style.setProperty("--theme-wipe-y", `${r.top + r.height / 2}px`);
  }

  // d2d-DetectHub eval F16: a toolbar is one tab stop with arrow keys between
  // its buttons (roving tabindex). The current mode holds the tab stop.
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const buttons = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>(".rc-theme-switcher__option"));
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    const next =
      e.key === "ArrowRight" || e.key === "ArrowDown" ? (i + 1) % buttons.length
      : e.key === "ArrowLeft" || e.key === "ArrowUp" ? (i - 1 + buttons.length) % buttons.length
      : e.key === "Home" ? 0
      : e.key === "End" ? buttons.length - 1
      : -1;
    if (next < 0) return;
    e.preventDefault();
    buttons[next].focus();
  }

  return (
    <div ref={ref} role="toolbar" aria-label={ariaLabel} className={cn("rc-theme-switcher", className)} onKeyDown={onKeyDown} {...rest}>
      {OPTIONS.map((opt) => {
        const Icon = opt.icon;
        return (
          <Tooltip key={opt.value} content={opt.value === "system" ? `${opt.label} — currently ${resolvedMode}` : opt.label}>
          <button
            type="button"
            tabIndex={mode === opt.value ? 0 : -1}
            aria-pressed={mode === opt.value}
            /* "System" alone does not say what you are actually looking at,
               and that is the one thing someone picking it needs confirmed.
               The resolved theme is folded into the accessible name, so a
               screen reader says "System, currently dark" instead of leaving
               the state to be inferred from the page. */
            aria-label={opt.value === "system" ? `${opt.label} (currently ${resolvedMode})` : opt.label}
            className="rc-theme-switcher__option"
            onClick={(e) => {
              setWipeOrigin(e.currentTarget);
              setMode(opt.value);
            }}
          >
            <Icon />
          </button>
          </Tooltip>
        );
      })}
    </div>
  );
});
